import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { isCurrentUserStaffOrSuperAdmin } from 'common/selectors/auth';
import { BodyText, Caption, H2, H3, PageContent, PageHeader, PageSection } from 'common/styles/page';
import { Button, Message } from 'semantic-ui-react';
import { Card } from 'common/components/Card';
import {
  useApiAdminV1SentryTestingTestBasicErrorCreateMutation,
  useApiAdminV1SentryTestingTestExceptionLoggingCreateMutation,
  useApiAdminV1SentryTestingTestTaskErrorCreateMutation,
  useApiAdminV1SentryTestingTestApiErrorCreateMutation,
  useApiAdminV1SentryTestingTestCustomExceptionCreateMutation,
  useApiAdminV1SentryTestingTestDatabaseErrorCreateMutation
} from 'store/generated/admin/sentry-testing';
import { Page } from 'react-pdf';

interface TestResult {
  testType: string;
  status: 'success' | 'error' | 'pending';
  message: string;
  timestamp: string;
}

const SentryTesting: React.FC = () => {
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const [testResults, setTestResults] = useState<TestResult[]>([]);

  // API mutations
  const [testBasicError, { isLoading: basicErrorLoading }] = useApiAdminV1SentryTestingTestBasicErrorCreateMutation();
  const [testExceptionLogging, { isLoading: exceptionLoading }] = useApiAdminV1SentryTestingTestExceptionLoggingCreateMutation();
  const [testTaskError, { isLoading: taskErrorLoading }] = useApiAdminV1SentryTestingTestTaskErrorCreateMutation();
  const [testApiError, { isLoading: apiErrorLoading }] = useApiAdminV1SentryTestingTestApiErrorCreateMutation();
  const [testCustomException, { isLoading: customExceptionLoading }] = useApiAdminV1SentryTestingTestCustomExceptionCreateMutation();
  const [testDatabaseError, { isLoading: databaseErrorLoading }] = useApiAdminV1SentryTestingTestDatabaseErrorCreateMutation();

  // Access control - only super admins can access this page
  if (!isAdmin) {
    return (
      <PageContent data-testid="sentry-testing-page">
        <PageHeader title="Access Denied" />
        <PageSection>
          <Message negative>
            You do not have permission to access this page. Only super administrators can access Sentry testing functionality.
          </Message>
        </PageSection>
      </PageContent>
    );
  }

  const addTestResult = (testType: string, status: 'success' | 'error', message: string) => {
    const result: TestResult = {
      testType,
      status,
      message,
      timestamp: new Date().toLocaleTimeString()
    };
    setTestResults(prev => [result, ...prev.slice(0, 9)]); // Keep last 10 results
  };

  const handleTestBasicError = async () => {
    try {
      const result = await testBasicError().unwrap() as { message?: string };
      addTestResult('Basic Error', 'success', result.message || 'Basic error logged successfully');
    } catch (error: any) {
      addTestResult('Basic Error', 'error', error.data?.error || 'Failed to test basic error logging');
    }
  };

  const handleTestExceptionLogging = async () => {
    try {
      const result = await testExceptionLogging().unwrap() as { message?: string };
      addTestResult('Exception Logging', 'success', result.message || 'Exception logged successfully');
    } catch (error: any) {
      addTestResult('Exception Logging', 'error', error.data?.error || 'Failed to test exception logging');
    }
  };

  const handleTestTaskError = async () => {
    try {
      const result = await testTaskError().unwrap() as { message?: string };
      addTestResult('Task Error', 'success', result.message || 'Task error logged successfully');
    } catch (error: any) {
      addTestResult('Task Error', 'error', error.data?.error || 'Failed to test task error logging');
    }
  };

  const handleTestApiError = async () => {
    try {
      const result = await testApiError().unwrap() as { message?: string };
      addTestResult('API Error', 'success', result.message || 'API error logged successfully');
    } catch (error: any) {
      addTestResult('API Error', 'error', error.data?.error || 'Failed to test API error logging');
    }
  };

  const handleTestCustomException = async () => {
    try {
      const result = await testCustomException().unwrap() as { message?: string };
      addTestResult('Custom Exception', 'success', result.message || 'Custom exception logged successfully');
    } catch (error: any) {
      addTestResult('Custom Exception', 'error', error.data?.error || 'Failed to test custom exception logging');
    }
  };

  const handleTestDatabaseError = async () => {
    try {
      const result = await testDatabaseError().unwrap() as { message?: string };
      addTestResult('Database Error', 'success', result.message || 'Database error logged successfully');
    } catch (error: any) {
      addTestResult('Database Error', 'error', error.data?.error || 'Failed to test database error logging');
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <PageContent data-testid="sentry-testing-page">
      <PageHeader title="Sentry Testing" />

        <Caption>
          This page allows you to test various Sentry error logging functionalities.
          All tests will generate actual error logs in Sentry for testing and debugging purposes.
        </Caption>

      <PageSection padtop>
        <Card>
          <CardHeader>
            <PageSection>
              <H2>Test Error Reporting</H2>
              <Caption>
                Click a button to trigger the corresponding Sentry error logging test
              </Caption>
            </PageSection>
            <Button onClick={clearResults} variant="secondary" size="small">
              Clear Results
            </Button>
          </CardHeader>

          <PageSection padbottom>
            <TestButtonsContainer>
              <TestButton
                onClick={handleTestBasicError}
                disabled={basicErrorLoading}
                data-testid="test-basic-error-btn"
              >
                {basicErrorLoading ? 'Testing...' : 'Basic Error'}
              </TestButton>

              <TestButton
                onClick={handleTestExceptionLogging}
                disabled={exceptionLoading}
                data-testid="test-exception-btn"
              >
                {exceptionLoading ? 'Testing...' : 'Exception Logging'}
              </TestButton>

              <TestButton
                onClick={handleTestTaskError}
                disabled={taskErrorLoading}
                data-testid="test-task-error-btn"
              >
                {taskErrorLoading ? 'Testing...' : 'Task Error'}
              </TestButton>

              <TestButton
                onClick={handleTestApiError}
                disabled={apiErrorLoading}
                data-testid="test-api-error-btn"
              >
                {apiErrorLoading ? 'Testing...' : 'API Error'}
              </TestButton>

              <TestButton
                onClick={handleTestCustomException}
                disabled={customExceptionLoading}
                data-testid="test-custom-exception-btn"
              >
                {customExceptionLoading ? 'Testing...' : 'Custom Exception'}
              </TestButton>

              <TestButton
                onClick={handleTestDatabaseError}
                disabled={databaseErrorLoading}
                data-testid="test-database-error-btn"
              >
                {databaseErrorLoading ? 'Testing...' : 'Database Error'}
              </TestButton>
            </TestButtonsContainer>
          </PageSection>


          {testResults.length === 0 ? (
            <NoResults>
              <p>No tests have been run yet. Use the buttons above to start testing.</p>
            </NoResults>
          ) : (
            <ResultsList>
              {testResults.map((result, index) => (
                <ResultItem key={index} status={result.status}>
                  <ResultHeader>
                    <ResultType>{result.testType}</ResultType>
                    <ResultTime>{result.timestamp}</ResultTime>
                  </ResultHeader>
                  <ResultMessage>{result.message}</ResultMessage>
                </ResultItem>
              ))}
            </ResultsList>
          )}
        </Card>
      </PageSection>
    </PageContent>
  );
};

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    color: #333;
  }

  p {
    margin: 0.5rem 0 0 0;
    color: #666;
    font-size: 0.9rem;
  }
`;

const TestButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  align-items: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TestButton = styled.button`
  flex: 0 0 auto;
  height: 40px;
  min-width: 90px;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: background-color 0.12s, transform 0.06s;

  &:hover:not(:disabled) {
    background-color: #e9ecef;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;

  p {
    margin: 0;
    font-style: italic;
  }
`;

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const ResultItem = styled.div<{ status: string }>`
  padding: 1rem;
  border-left: 4px solid ${props =>
    props.status === 'success' ? '#28a745' :
      props.status === 'error' ? '#dc3545' : '#ffc107'
  };
  background-color: ${props =>
    props.status === 'success' ? '#f8fff9' :
      props.status === 'error' ? '#fff8f8' : '#fffbf0'
  };
  margin-bottom: 0.5rem;
  border-radius: 4px;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ResultType = styled.span`
  font-weight: 600;
  color: #333;
`;

const ResultTime = styled.span`
  font-size: 0.8rem;
  color: #666;
`;

const ResultMessage = styled.p`
  margin: 0;
  color: #555;
  font-size: 0.9rem;
`;

export default SentryTesting;
